import React, { useEffect, useState, useRef, useCallback } from 'react';
import { OptImg } from '../components/ui/OptImg';
import { GalleryLightbox } from '../components/website/GalleryLightbox';
import { motion } from 'framer-motion';
import { Layout } from '../components/layout/Layout';
import { Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const filters = [
  { id: 'all', name: 'All' },
  { id: 'residential', name: 'Residential' },
  { id: 'commercial', name: 'Commercial' },
  { id: 'roller-shades', name: 'Roller Shades' },
  { id: 'motorized-drapes', name: 'Motorized Drapes' },
  { id: 'roman-shades', name: 'Roman Shades' },
];

// Interactive gallery (filters, infinite scroll, lightbox). Used by the coded page AND the builder's gallery 'interactive' layout.
export const GalleryBrowser = ({ embedded = false, stickyFilters = !embedded }) => {
  const [projects, setProjects] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // Fetch initial projects
  useEffect(() => {
    setProjects([]);
    setPage(1);
    setHasMore(true);
    fetchProjects(1, true);
  }, [activeFilter]);

  const fetchProjects = async (pageNum, isInitial = false) => {
    if (isInitial) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = { page: pageNum, limit: 12 };
      
      // Apply filter
      if (activeFilter !== 'all') {
        if (['residential', 'commercial'].includes(activeFilter)) {
          params.category = activeFilter;
        } else {
          params.product = activeFilter;
        }
      }

      const response = await axios.get(`${API_URL}/gallery/paginated`, { params });
      const { items, has_more, total: totalCount } = response.data;
      
      if (isInitial) {
        setProjects(items);
      } else {
        setProjects(prev => [...prev, ...items]);
      }
      
      setHasMore(has_more);
      setTotal(totalCount);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      // Fallback to non-paginated endpoint
      if (isInitial) {
        try {
          const fallbackResponse = await axios.get(`${API_URL}/gallery`);
          setProjects(fallbackResponse.data);
          setHasMore(false);
        } catch (e) {
          console.error('Fallback fetch failed:', e);
        }
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Intersection Observer for infinite scroll
  const handleObserver = useCallback((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting && hasMore && !loadingMore && !loading) {
      fetchProjects(page + 1);
    }
  }, [hasMore, loadingMore, loading, page]);

  useEffect(() => {
    const option = {
      root: null,
      rootMargin: '100px',
      threshold: 0
    };
    
    observerRef.current = new IntersectionObserver(handleObserver, option);
    
    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver]);

  return (
    <>
      {/* Filters */}
      <section className={`py-8 border-b border-neutral-200 bg-white z-10 ${stickyFilters ? 'sticky top-20' : ''}`}>
        <div className="container-xl">
          <div className="flex flex-wrap gap-2 items-center">
            {filters.map((filter) => (
              <button
                key={filter.id}
                data-testid={`gallery-filter-${filter.id}`}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  activeFilter === filter.id ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {filter.name}
              </button>
            ))}
            {total > 0 && (
              <span className="ml-auto text-sm text-neutral-500">
                Showing {projects.length} of {total}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding">
        <div className="container-xl">
          <h2 className="sr-only">Window treatment design inspiration</h2>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="aspect-square bg-neutral-200 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-neutral-500 text-lg">No projects found for this filter</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {projects.map((project, index) => (
                  <motion.div
                    key={`${project.id}-${index}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min((index % 12) * 0.05, 0.3) }}
                    className="relative aspect-square overflow-hidden group rounded-lg cursor-zoom-in focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                    data-testid={`gallery-card-${index}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`View ${project.title}`}
                    onClick={() => setLightbox(index)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setLightbox(index); } }}
                  >
                    {/* Blurhash placeholder */}
                    {project.blurhash && (
                      <div className="absolute inset-0 bg-neutral-200" />
                    )}
                    
                    <OptImg
                      sizes="(min-width: 768px) 33vw, 50vw"
                      src={project.image?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${project.image}` : project.image} 
                      alt={project.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400';
                      }}
                    />
                    
                    {/* AI badge */}
                    {project.is_ai_generated && (
                      <div className="absolute top-3 left-3 bg-purple-500 text-white px-2 py-0.5 rounded text-xs font-medium">
                        AI
                      </div>
                    )}
                    
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-neutral-900/0 group-hover:bg-neutral-900/70 transition-colors flex items-end p-6">
                      <div className="translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                        <p className="text-white font-serif text-xl">{project.title}</p>
                        <p className="text-neutral-300 text-sm mt-1 line-clamp-2">{project.description}</p>
                        <span className="inline-block mt-3 text-xs uppercase tracking-wider text-amber-400">
                          {(project.product || '').replace('-', ' ')}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Load more trigger */}
              <div ref={loadMoreRef} className="h-20 flex items-center justify-center mt-8">
                {loadingMore && (
                  <div className="flex items-center gap-2 text-neutral-500">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Loading more...</span>
                  </div>
                )}
                {!hasMore && projects.length > 0 && (
                  <p className="text-neutral-400 text-sm">You've seen all {total}</p>
                )}
              </div>
              {lightbox !== null && <GalleryLightbox projects={projects} index={lightbox} onIndex={setLightbox} onClose={() => setLightbox(null)} />}
            </>
          )}
        </div>
      </section>
    </>
  );
};

const GalleryHero = () => (
      <section className="pt-32 pb-20 bg-neutral-50">
        <div className="container-xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="label-overline text-amber-700">Gallery</span>
            <h1 className="mt-4 text-neutral-900">Inspiration Gallery</h1>
            <p className="mt-6 text-xl text-neutral-600 leading-relaxed">
              Design inspiration for custom shades, blinds and drapery in residential and commercial spaces.
            </p>
          </motion.div>
        </div>
      </section>
);

export const GalleryPage = () => <Layout><GalleryHero /><GalleryBrowser /></Layout>;
